import {
    Dialog,
    DialogActions,
    DialogContent,
    DialogContentText,
    DialogTitle,
    Grid,
    IconButton,
    SxProps,
    Typography,
} from "@mui/material";
import CloseIcon from "@mui/icons-material/Close";
import React from "react";

import AcxButton from "../AcxButton";
import theme from "Theme/AppTheme";
import { sxClsx } from "utils/styleClsx";

type Props = {
    isOpen: boolean;
    onClose: () => void;
    buttonsDisabled?: boolean;
    title: React.ReactNode;
    subTitle?: string;
    text?: string;
    dialogContentChildren?: any;
    maxWidth?: "xs" | "sm" | "md" | "lg" | "xl" | false;
    contentWidth?: string;
    children?: React.ReactNode;
    disableEnforceFocus?: boolean;
    textAlignCenterContent?: boolean;
    textAlignCenterTitle?: boolean;
    hideCloseButton?: boolean;
    hideCancelButton?: boolean;
    disableBackdropClose?: boolean;
    centerActionButtons?: boolean;
    fullWidth?: boolean;
    fullScreen?: boolean;
    sx?: SxProps;
    dialogContentSx?: SxProps;
    id?: string;
    cancelButtonId?: string;
};

const AcxDialog: React.FC<Props> = (props) => {
    const {
        children,
        isOpen,
        onClose,
        buttonsDisabled,
        title,
        subTitle,
        text,
        dialogContentChildren,
        maxWidth,
        contentWidth,
        disableEnforceFocus,
        textAlignCenterContent,
        textAlignCenterTitle,
        hideCloseButton,
        hideCancelButton,
        disableBackdropClose,
        fullWidth,
        fullScreen,
        sx,
        dialogContentSx,
        id = "",
    } = props;

    const hasSubtitle = Boolean(subTitle);
    const handleClose = (_event: object, reason: string) => {
        if (disableBackdropClose && reason === "backdropClick") {
            return;
        }

        onClose();
    };

    return (
        <Dialog
            sx={sx}
            open={isOpen}
            onClose={handleClose}
            maxWidth={maxWidth}
            fullWidth={fullWidth}
            disableEnforceFocus={disableEnforceFocus}
            disableRestoreFocus
            fullScreen={fullScreen}
            hideBackdrop={fullScreen}
            style={
                fullScreen
                    ? {
                          height: `calc(100% - 72px)`,
                          width: "100dvw",
                          top: "72px",
                          left: "0px",
                          right: "0px",
                          bottom: "0px",
                          position: "relative",
                          zIndex: 1299,
                      }
                    : {}
            }
            PaperProps={
                fullScreen
                    ? {
                          style: {
                              boxShadow: "none",
                              backgroundColor: theme.palette.lightgray.main,
                          },
                      }
                    : { sx: { borderRadius: 2 } }
            }
        >
            <DialogTitle
                id="alert-dialog-title"
                sx={{ opacity: 1, padding: theme.spacing(3) }}
            >
                <Grid
                    container
                    direction="row"
                    justifyContent="space-between"
                    alignItems={hasSubtitle ? "flex-start" : "center"}
                    flexWrap="nowrap"
                    columnGap={1}
                >
                    <Grid container item direction="column">
                        <Grid item>
                            <Typography
                                component="div"
                                variant="h1"
                                align={textAlignCenterTitle ? "center" : "left"}
                                sx={{
                                    fontFamily: theme.typography.fontFamily,
                                    fontWeight:
                                        theme.typography.fontWeightSemibold,
                                    fontSize: "20px",
                                    lineHeight: "32px",
                                    color: theme.palette.gray[700],
                                }}
                            >
                                {title || "Please Confirm..."}
                            </Typography>
                        </Grid>
                        {hasSubtitle && (
                            <Grid item>
                                <Typography
                                    component="span"
                                    variant="subtitle2"
                                    sx={{
                                        fontWeight:
                                            theme.typography.fontWeightRegular,
                                        fontSize: 14,
                                        lineHeight: "20px",
                                        color: theme.palette.gray[500],
                                    }}
                                    color={"textSecondary"}
                                >
                                    {subTitle}
                                </Typography>
                            </Grid>
                        )}
                    </Grid>

                    {!hideCloseButton && (
                        <Grid item>
                            <IconButton
                                aria-label="close"
                                disabled={buttonsDisabled}
                                onClick={onClose}
                                id={`close-dialog-button-${id}`}
                            >
                                <CloseIcon />
                            </IconButton>
                        </Grid>
                    )}
                </Grid>
            </DialogTitle>

            <DialogContent
                sx={sxClsx(
                    {
                        fontSize: "13px",
                        color: theme.palette.black.main,
                        height: "100%",
                        padding: theme.spacing(3),
                    },
                    dialogContentSx,
                )}
                style={contentWidth ? { width: contentWidth } : {}}
            >
                <DialogContentText
                    style={text ? {} : { margin: 0 }}
                    sx={{
                        textAlign: textAlignCenterContent ? "center" : "left",
                        paddingBottom: "10px",
                    }}
                >
                    {text}
                </DialogContentText>

                {dialogContentChildren}
            </DialogContent>

            <DialogActions
                style={
                    fullScreen
                        ? {
                              backgroundColor: theme.palette.white.main,
                          }
                        : {}
                }
                sx={{
                    justifyContent: props.centerActionButtons
                        ? "center"
                        : "end",
                    padding: theme.spacing(3),
                }}
            >
                {!hideCancelButton && (
                    <AcxButton
                        disabled={buttonsDisabled ?? false}
                        onClick={onClose}
                        color="white"
                        id={props.cancelButtonId ?? "acx-dialog-cancel-button"}
                    >
                        Cancel
                    </AcxButton>
                )}

                {children}
            </DialogActions>
        </Dialog>
    );
};

export default AcxDialog;
